/**
 * <TutorHomeGreeting> — le haut de l'accueil du masteur : « Bonjour Camille », la date et le nombre
 * de lives du jour (maquette A1).
 *
 * À la place de l'ancien titre « TABLEAU DE BORD ». Son bouton de rafraîchissement reste, en face
 * du salut (`<RefreshButton>`, la flèche qui tourne dans son bouton) : sur le web, il n'y a pas de
 * tirer-pour-rafraîchir, et l'ancien écran le donnait. Le salut est le seul gros texte de la tête.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { RefreshButton } from "../../molecules/RefreshButton"
import { formatDayLabel } from "../../../ports/primary/ui/formatters/dateFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"

const greeting = (firstname: string) => `Bonjour ${firstname}`

/** « Mercredi 28 octobre · 2 lives aujourd'hui », « … · aucun live aujourd'hui ». */
function dayLine(livesToday: number, date: string): string {
    const lives = livesToday === 0 ? "aucun live aujourd'hui" : `${countLabel(livesToday, "live")} aujourd'hui`
    return `${formatDayLabel(date, false)} · ${lives}`
}

export type TutorHomeGreetingProps = {
    firstname: string
    livesToday: number
    /** Le jour écrit — aujourd'hui par défaut. */
    date?: string
    refreshing?: boolean
    /** Rafraîchir l'accueil. Absent : pas de bouton. */
    onRefresh?: () => void
}

export function TutorHomeGreeting(props: TutorHomeGreetingProps) {
    const { firstname, livesToday, date = new Date().toISOString(), refreshing = false, onRefresh } = props

    return (
        <Row gap="3" justify="between" align="center">
            <Stack gap="1" style={styles.text}>
                <Title size="text-3xl" numberOfLines={1}>
                    {greeting(firstname)}
                </Title>
                <Text size="text-md" color="text-muted">
                    {dayLine(livesToday, date)}
                </Text>
            </Stack>
            {onRefresh != null && (
                <View>
                    <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />
                </View>
            )}
        </Row>
    )
}

/** Sa forme pendant le premier chargement : le prénom est connu, il s'écrit ; la ligne du jour attend. */
export function TutorHomeGreetingSkeleton(props: { firstname?: string }) {
    const { firstname } = props
    return (
        <Stack gap="1">
            {firstname != null ? (
                <Title size="text-3xl" numberOfLines={1}>
                    {greeting(firstname)}
                </Title>
            ) : (
                <Skeleton textSize="text-3xl" width={220} />
            )}
            <Skeleton textSize="text-md" width={240} />
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    text: { flex: 1, flexShrink: 1 },
}))
